import type { ReactElement } from 'react';
import { useState } from 'react';
import type { MeetingDetail } from '@contracts/meetings';
import { Button, Card, Modal } from '../ui';
import { timecode } from './Player';

type Shot = NonNullable<MeetingDetail['screenshots']>[number];

/**
 * The frames people asked for out loud — "Ptitim, screenshot" (PRD 64).
 *
 * Vladimir, 2026-09-26: *"if on the meeting user of Ptitim says to capture the
 * screen then it will get screenshot from the video recording and add it to the
 * meeting"*. They are cut after the call from the video, so a frame asked for
 * during a meeting still going says so rather than showing an empty box.
 *
 * Placed on the recording's clock (`video.startedAt`), not on the run's own:
 * a meeting recorded twice keeps its first video, and the player plays that.
 */
export function Screenshots({
  meeting,
  onPlay,
}: {
  meeting: MeetingDetail;
  onPlay: ((seconds: number) => void) | null;
}): ReactElement | null {
  const [open, setOpen] = useState<Shot | null>(null);
  const shots = meeting.screenshots ?? [];
  if (shots.length === 0) return null;
  const zero = meeting.video.startedAt ? Date.parse(meeting.video.startedAt) : null;
  const secondsOf = (shot: Shot): number =>
    zero === null ? shot.offsetSeconds : Math.max(0, (Date.parse(shot.at) - zero) / 1000);

  return (
    <Card title={`Screenshots · ${shots.length}`}>
      <div className="shots" aria-label="Screenshots">
        {shots.map((shot) => (
          <figure key={shot.id} className="shot">
            {shot.status === 'READY' && shot.url ? (
              <button
                type="button"
                className="shot-thumb"
                aria-label={`Screenshot at ${timecode(secondsOf(shot))}`}
                onClick={() => setOpen(shot)}
              >
                <img src={shot.url} alt="" loading="lazy" />
              </button>
            ) : (
              <div className="shot-thumb shot-waiting">
                {shot.status === 'PENDING'
                  ? 'Taken after the call'
                  : shot.status === 'READY'
                    ? 'Not available just now'
                    : 'Could not be taken'}
              </div>
            )}
            <figcaption className="mono">
              {timecode(secondsOf(shot))}
              {shot.askedBy ? ` · ${shot.askedBy}` : ''}
            </figcaption>
          </figure>
        ))}
      </div>
      {open?.url ? (
        <Modal
          title={`Screenshot at ${timecode(secondsOf(open))}`}
          onClose={() => setOpen(null)}
          foot={
            <>
              <a className="btn" href={open.url} target="_blank" rel="noreferrer">
                Open the image
              </a>
              {onPlay ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    onPlay(secondsOf(open));
                    setOpen(null);
                  }}
                >
                  Play from here
                </Button>
              ) : null}
            </>
          }
        >
          <img
            className="shot-full"
            src={open.url}
            alt={`The meeting at ${timecode(secondsOf(open))}`}
          />
        </Modal>
      ) : null}
    </Card>
  );
}
